import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  type ContentFramework,
  type ContentFrameworkColumn,
  type ContentFrameworkPrinciple,
  type ContentFrameworkTopic,
} from "@/lib/composerCore";
import { tierTopicTreeColumnCap, tierTopicsPerColumnCap, type Tier } from "@/lib/tier";
import { useTopicUsage } from "@/hooks/useTopicUsage";
import { EmptyColumnSlot } from "./topic-tree/EmptyColumnSlot";
import { FilledColumnCard } from "./topic-tree/FilledColumnCard";
import { StartTopicTreeCard } from "./topic-tree/StartTopicTreeCard";

type Props = {
  workspaceId: string;
  workspaceTier: Tier | null | undefined;
  framework: ContentFramework | null;
  columns: ContentFrameworkColumn[];
  principles: ContentFrameworkPrinciple[];
  topics: ContentFrameworkTopic[];
  onChange: () => void | Promise<void>;
};

/**
 * TopicTreeEditor (Pass D)
 *
 * Vertical-column rebuild of the Topic Tree. One card per column slot up to the
 * `column_count_target` (capped by tier). Empty slots render an EmptyColumnSlot
 * placeholder so the user can fill the tree progressively.
 */
export function TopicTreeEditor({
  workspaceId,
  workspaceTier,
  framework,
  columns,
  principles,
  topics,
  onChange,
}: Props) {
  const columnCap = tierTopicTreeColumnCap(workspaceTier);
  const topicCap = tierTopicsPerColumnCap(workspaceTier);
  const [savingTarget, setSavingTarget] = useState(false);
  const { usage, refresh: refreshUsage } = useTopicUsage(workspaceId);
  const handleChange = async () => {
    await onChange();
    await refreshUsage();
  };
  const [showElaborations, setShowElaborations] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("composer.topicTree.viewMode") === "elaborations";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("composer.topicTree.viewMode", showElaborations ? "elaborations" : "compact");
  }, [showElaborations]);

  const target = framework
    ? Math.min(Math.max(framework.column_count_target ?? 3, 1), columnCap)
    : 0;

  const slots = useMemo(() => {
    const sorted = [...columns].sort((a, b) => a.position - b.position);
    return Array.from({ length: target }, (_, idx) => sorted[idx] ?? null);
  }, [columns, target]);

  if (!framework) {
    return (
      <StartTopicTreeCard
        workspaceId={workspaceId}
        columnCap={columnCap}
        onCreated={handleChange}
      />
    );
  }

  const updateColumnCountTarget = async (next: number) => {
    const clamped = Math.min(Math.max(next, 1), columnCap);
    setSavingTarget(true);
    const { error } = await supabase
      .from("content_frameworks")
      .update({ column_count_target: clamped })
      .eq("id", framework.id);
    setSavingTarget(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    await onChange();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Columns</span>
          <Select
            value={String(target)}
            onValueChange={(value) => void updateColumnCountTarget(Number(value))}
            disabled={savingTarget}
          >
            <SelectTrigger className="h-8 w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: columnCap }, (_, idx) => idx + 1).map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">cap {columnCap}</span>
        </div>
        <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
          <Button
            type="button"
            size="sm"
            variant={showElaborations ? "ghost" : "secondary"}
            className="h-7 px-2 text-xs"
            onClick={() => setShowElaborations(false)}
          >
            Compact
          </Button>
          <Button
            type="button"
            size="sm"
            variant={showElaborations ? "secondary" : "ghost"}
            className="h-7 px-2 text-xs"
            onClick={() => setShowElaborations(true)}
          >
            Elaborations
          </Button>
        </div>
      </div>

      <div className={showElaborations ? "flex flex-col gap-4" : "grid gap-4 md:grid-cols-2 xl:grid-cols-3"}>
        {slots.map((column, idx) =>
          column ? (
            <FilledColumnCard
              key={column.id}
              workspaceId={workspaceId}
              column={column}
              allColumns={columns}
              principles={principles.filter((p) => p.framework_column_id === column.id)}
              topics={topics.filter((t) => t.framework_column_id === column.id && !t.parent_topic_id)}
              allTopics={topics}
              usage={usage}
              topicCap={topicCap}
              showElaborations={showElaborations}
              onChange={handleChange}
            />
          ) : (
            <EmptyColumnSlot
              key={`slot-${idx}`}
              workspaceId={workspaceId}
              framework={framework}
              position={idx}
              onChange={handleChange}
            />
          )
        )}
      </div>
    </div>
  );
}
